import { useState } from "react";
import { MapPin, Mail, Phone, Send, CheckCircle, Linkedin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ_ITEMS = [
  { q: "Comment puis-je soutenir EDUCPEOPLE ?", a: "Vous pouvez nous soutenir de plusieurs manieres : faire un don via HelloAsso, devenir benevole ou mentor, ou proposer un partenariat entreprise. Rendez-vous sur notre page Soutenir pour plus d'informations." },
  { q: "Mes dons sont-ils deductibles des impots ?", a: "Oui, EDUCPEOPLE etant une association d'interet general, vos dons sont deductibles a hauteur de 66% de votre impot sur le revenu (dans la limite de 20% du revenu imposable)." },
  { q: "Comment participer a un atelier ou une formation ?", a: "Nos programmes sont accessibles via inscription sur notre site ou en nous contactant directement. Les conditions d'acces varient selon les programmes." },
  { q: "EDUCPEOPLE intervient-elle hors Ile-de-France ?", a: "Actuellement, nos actions sont principalement concentrees en Ile-de-France, mais nous avons l'ambition de nous deployer sur d'autres territoires dans les prochaines annees." },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", rgpd_consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "", rgpd_consent: false });
    } catch {
      setError("Une erreur est survenue. Veuillez reessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400 mb-6">
            Contact
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-50 mb-8 leading-[1.1]">
            Parlons de votre projet
          </h1>
        </div>
      </section>

      <div className="section-divider" />

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Form */}
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-light text-slate-50 mb-8">
              Envoyez-nous un message
            </h2>

            {sent ? (
              <div data-testid="contact-success" className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-8 flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-body text-base font-medium text-emerald-400 mb-1">Message envoye !</h3>
                  <p className="font-body text-sm text-slate-400">Nous vous repondrons dans les meilleurs delais.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-body text-sm text-slate-300">Nom complet</Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      className="bg-slate-900/50 border-white/10 text-slate-50 focus:border-blue-500/50 focus:ring-blue-500/20"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-body text-sm text-slate-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      className="bg-slate-900/50 border-white/10 text-slate-50 focus:border-blue-500/50 focus:ring-blue-500/20"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="font-body text-sm text-slate-300">Sujet</Label>
                  <Input
                    id="subject"
                    data-testid="contact-subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    required
                    className="bg-slate-900/50 border-white/10 text-slate-50 focus:border-blue-500/50 focus:ring-blue-500/20"
                    placeholder="Objet de votre message"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="font-body text-sm text-slate-300">Message</Label>
                  <textarea
                    id="message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    rows={5}
                    className="w-full rounded-lg bg-slate-900/50 border border-white/10 text-slate-50 p-3 font-body text-sm focus:border-blue-500/50 focus:ring-blue-500/20 focus:outline-none focus:ring-2 resize-none"
                    placeholder="Votre message..."
                  />
                </div>
                {error && <p className="font-body text-sm text-red-400">{error}</p>}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="rgpd"
                    checked={form.rgpd_consent}
                    onChange={(e) => setForm({ ...form, rgpd_consent: e.target.checked })}
                    className="mt-1 rounded"
                    data-testid="contact-rgpd-consent"
                    required
                  />
                  <label htmlFor="rgpd" className="font-body text-xs text-slate-400 leading-relaxed">
                    J'accepte que mes donn{"\u00e9"}es soient trait{"\u00e9"}es par EDUCPEOPLE.org pour r{"\u00e9"}pondre {"\u00e0"} ma demande,
                    conform{"\u00e9"}ment {"\u00e0"} la{" "}
                    <a href="/confidentialite" target="_blank" className="text-blue-400 hover:text-blue-300 underline">
                      politique de confidentialit{"\u00e9"}
                    </a>. *
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-8 py-3 rounded-full font-body font-medium transition-all duration-300 disabled:opacity-50"
                >
                  {sending ? "Envoi en cours..." : "Envoyer le message"}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>

          {/* Info + FAQ */}
          <div className="space-y-10">
            <div>
              <h3 className="font-heading text-xl font-medium text-slate-50 mb-6">Coordonnees</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-body text-sm font-medium text-slate-50">Adresse</p>
                    <p className="font-body text-sm text-slate-400">40 Rue du Pave des Gardes, 92370 Chaville</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-body text-sm font-medium text-slate-50">Email</p>
                    <a href="mailto:contact@educpeople.org" className="font-body text-sm text-blue-400">contact@educpeople.org</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-body text-sm font-medium text-slate-50">Telephone</p>
                    <p className="font-body text-sm text-slate-400">+33 9 72 38 65 14</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#0A66C2]/20 flex items-center justify-center shrink-0">
                    <Linkedin className="w-5 h-5 text-[#0A66C2]" />
                  </div>
                  <div>
                    <p className="font-body text-sm font-medium text-slate-50">LinkedIn</p>
                    <a href="https://www.linkedin.com/company/educpeople/" target="_blank" rel="noopener noreferrer" className="font-body text-sm text-blue-400 hover:text-blue-300 transition-colors" data-testid="contact-linkedin">
                      Suivre EDUCPEOPLE
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="section-divider" />

            <div>
              <h3 className="font-heading text-xl font-medium text-slate-50 mb-6">Questions frequentes</h3>
              <Accordion type="single" collapsible className="space-y-2" data-testid="contact-faq">
                {FAQ_ITEMS.map((item, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                    <AccordionTrigger className="font-body text-sm text-slate-200 hover:text-blue-400 py-4">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="font-body text-sm text-slate-400 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
